import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import routes from "@/routes";

import {
  Flex,
  Typography,
  Spin,
  App,
  Space,
  Collapse,
  Button,
  Tooltip,
  Modal,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";

import PencilIcon from "@/icons/PencilIcon";
import SaveAsIcon from "@/icons/SaveAsIcon";

import FormFieldsTable from "@/components/tables/FormFieldsTable";
import FiltersGroupModal from "@/components/modals/FiltersGroupModal";
import FilterModal from "@/components/modals/FilterModal";

import { getItem } from "@/api/definitions";
import {
  deleteFilter,
  updateFilter,
  synchronizeFilters,
} from "@/api/definitionFilters";

import { MODAL_TITLES } from "@/constants/definitions";

const { Title } = Typography;

// const DEFAULT_FIELDS = [
//   {
//     caption: "Поиск по названию",
//     field: "",
//     field_name: "",
//     idx: -2,
//     uuid: "search",
//     disable: true,
//   },
//   {
//     caption: "Рабочая группа",
//     field: "",
//     field_name: "",
//     idx: -1,
//     uuid: "unit",
//     disable: true,
//   },
// ];

const Filters = () => {
  const { id, type, formId } = useParams();
  const { message } = App.useApp();

  const [data, setData] = useState();

  const [isLoading, setIsLoading] = useState(true);

  const [currentGroup, setCurrentGroup] = useState();
  const [currentFilter, setCurrentFilter] = useState();

  const [isGroupModalShow, setIsGroupModalShow] = useState(false);
  const [addFieldModalShow, setAddFieldModalShow] = useState(false);

  const groups = useMemo(() => {
    if (!data) return [];

    return data.filterGroups.map((group, idx) => {
      // if (idx === 0)
      //   return {
      //     ...group,
      //     filters: [...DEFAULT_FIELDS, ...(data.filterGroups[0].filters || [])],
      //   };
      // else
      return {
        ...group,
        filters: group.filters || [],
      };
    });
  }, [data]);

  // initial fetch
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [id, type, formId]);

  const fetchData = async () => {
    setIsLoading(true);

    try {
      const data = await getItem(id);
      setData(data);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditGroup = (e, group) => {
    e.stopPropagation();
    setCurrentGroup(group);
    setIsGroupModalShow(true);
  };

  const handleAddField = (group) => {
    setCurrentFilter({
      definition_uuid: group.host,
      group: group.uuid,
      idx: group.filters?.length || 0,
    });
    setAddFieldModalShow(true);
  };

  const handleDeleteField = (item) => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить фильтр ${item.caption}?`,
      content: "Это действие нельзя будет отменить.",

      onOk() {
        handleConfirmDelete(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
      zIndex: 99999,
    });
  };

  const handleConfirmDelete = async (item) => {
    try {
      await deleteFilter({ uuid: item.uuid });
      fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  const handleCreateGroup = () => {
    setCurrentGroup({ definition_uuid: id, idx: groups.length });
    setIsGroupModalShow(true);
  };

  const handleFilterChange = async (item) => {
    try {
      await updateFilter(item);
      const data = await getItem(id);
      setData(data);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  const handleSynchronize = async () => {
    Modal.confirm({
      title: `Вы уверены, что хотите синхронизировать группы полей из объектной формы с фильтрами?`,
      content:
        "Это удалит текущую группировку и заменит ее на новую. Это действие нельзя будет отменить.",

      onOk() {
        handleConfirmSynchronize();
      },
      width: 470,
      okText: "Синхронизировать",
      cancelText: "Отмена",
      zIndex: 99999,
    });
  };

  const handleConfirmSynchronize = async () => {
    setIsLoading(true);
    try {
      await synchronizeFilters(id);
      fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Flex vertical gap={30}>
            <Link
              to={routes.administration.resourcesSettings.structures.definitionDetail(
                type,
                id
              )}
            >
              <Space>
                <ArrowLeftOutlined />
                <span>Назад в {MODAL_TITLES[type]}</span>
              </Space>
            </Link>
            {data && (
              <Flex gap={20} vertical>
                <Flex gap={20} justify="space-between" align="center">
                  <Title level={2} className="no-margin">
                    Настройка фильтров для объекта «{data.name}»
                  </Title>

                  <Button
                    icon={<SaveAsIcon />}
                    color="primary"
                    variant="outlined"
                    onClick={handleSynchronize}
                  >
                    Синхронизация
                  </Button>
                </Flex>

                <Collapse
                  bordered={false}
                  defaultActiveKey={groups.map((group) => group.uuid)}
                  items={groups.map((group) => {
                    return {
                      key: group.uuid,
                      label: (
                        <Title level={5} className="no-margin">
                          {group.name}
                        </Title>
                      ),
                      extra: (
                        <Flex>
                          {!!group.idx && (
                            <Tooltip
                              title="Редактировать группу"
                              placement="bottomRight"
                            >
                              <Button
                                color="default"
                                variant="text"
                                type="text"
                                icon={<PencilIcon />}
                                onClick={(e) => handleEditGroup(e, group)}
                                style={{ padding: 0 }}
                              />
                            </Tooltip>
                          )}
                        </Flex>
                      ),
                      children: (
                        <FormFieldsTable
                          data={group.filters}
                          // incrIdx={group.idx === 0 ? 2 : 0}
                          pagination={false}
                          showDescription={false}
                          showMulti
                          onAddField={() => handleAddField(group)}
                          onChange={handleFilterChange}
                          onDeleteItem={handleDeleteField}
                        />
                      ),
                      style: {
                        marginBottom: 24,
                        background: "var(--color-white)",
                        borderRadius: "4px",
                        border: "none",
                      },
                    };
                  })}
                />

                <div>
                  <Button type="primary" onClick={handleCreateGroup}>
                    Добавить группу
                  </Button>
                </div>
              </Flex>
            )}
          </Flex>

          {isGroupModalShow && (
            <FiltersGroupModal
              visible={isGroupModalShow}
              onCancel={() => setIsGroupModalShow(false)}
              data={currentGroup}
              onSave={() => {
                setIsGroupModalShow(false);
                fetchData();
              }}
            />
          )}

          {addFieldModalShow && (
            <FilterModal
              visible={addFieldModalShow}
              onCancel={() => setAddFieldModalShow(false)}
              data={currentFilter}
              onSave={() => {
                setAddFieldModalShow(false);
                fetchData();
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default Filters;
